import type { DocumentNode, OperationDefinitionNode } from 'graphql';
import { addBreadcrumb } from '@sentry/vue';
import { cacheExchange } from '@urql/exchange-graphcache';
import { createClient, fetchExchange, mapExchange } from '@urql/vue';
import { useNarrative } from '@/composables/useNarrative';
import usePopupNotifications from '@/composables/usePopupNotifications';
import { getLoginUrlWithCurrentPageRedirect } from '@/helpers/getLoginUrlWithCurrentPageRedirect';
import { appReady } from '@/state/authState';
import { useSessionExpiredStore } from '@/stores/overlays/sessionExpired';
import { lighthouseSubscriptionExchange } from './lighthouseSubscriptionExchange';

const getOperationName = (doc: DocumentNode): string => {
    const def = doc.definitions.find((d): d is OperationDefinitionNode => d.kind === 'OperationDefinition');
    return def?.name?.value ?? 'unknown';
};

const getCsrfToken = (): string => {
    const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
    return (match?.[1] != null && match[1] !== '') ? decodeURIComponent(match[1]) : '';
};

const syntheticErrorResponse = (message: string): Response => new Response(
    JSON.stringify({ errors: [{ message }] }),
    { status: 200, headers: { 'Content-Type': 'application/json' } },
);

const showSentryFeedback = (): void => {
    import('@/composables/useSentry').then((module) => {
        module.showSentryFeedback();
    });
};

const customFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await fetch(input, init);

    switch (response.status) {
        case 401:
        case 419:
            if (appReady.value) {
                useSessionExpiredStore().setSessionExpired(true);
                return syntheticErrorResponse('Your session has expired. Please log in again.');
            }

            window.location.href = getLoginUrlWithCurrentPageRedirect();
            return new Promise<Response>(() => {});
        case 402:
            window.location.href = '/settings#/subscription';
            return syntheticErrorResponse('A subscription is required to perform this action.');
        case 403: {
            const body: { message?: string; errors?: Array<{ message: string }> } | null = await response.clone().json().catch(() => null);
            const message = body?.message ?? body?.errors?.[0]?.message ?? 'You do not have permission to perform this action.';
            return syntheticErrorResponse(message);
        }
        case 500:
            showSentryFeedback();
            return syntheticErrorResponse('An unexpected server error occurred.');
        case 502:
            return syntheticErrorResponse('The server is temporarily unavailable.');
        case 503:
            return syntheticErrorResponse('Service unavailable.');
        case 504:
        case 524:
            return syntheticErrorResponse('The request timed out.');
    }

    const contentType = response.headers.get('content-type');
    if (contentType == null || !contentType.includes('application/json')) {
        addBreadcrumb({
            type: 'http',
            category: 'graphql.response',
            message: `${response.status} GraphQL (non-JSON response intercepted)`,
            data: {
                status_code: response.status,
                content_type: contentType,
                response_body: await response.clone().text().then(t => t.slice(0, 1000)).catch(() => '[unreadable]'),
            },
            level: 'warning',
        });

        usePopupNotifications().notifyError('An unexpected response was received from the server. Please check your network connection and try again.');

        return syntheticErrorResponse('Received non-JSON response from server');
    }

    const sessionExpiresAt = response.headers.get('x-session-expires-at');
    if (sessionExpiresAt !== null && sessionExpiresAt !== '') {
        useSessionExpiredStore().setSessionExpiresAt(sessionExpiresAt);
    }

    return response;
};

const getHeaders = (): Record<string, string> => {
    const { currentMerchantOptional, currentServiceProviderOptional } = useNarrative();

    const headers: Record<string, string> = {
        'Accept': 'application/json',
        'X-App-Client': `conveyr-ui/${(import.meta.env.VITE_RELEASE_TAG != null && import.meta.env.VITE_RELEASE_TAG !== '') ? import.meta.env.VITE_RELEASE_TAG : 'unknown'}`,
        'X-XSRF-TOKEN': getCsrfToken(),
    };

    if (currentMerchantOptional.value) {
        headers['X-Selected-Client-Id'] = currentMerchantOptional.value.id.toString();
    }

    if (currentServiceProviderOptional.value) {
        headers['X-Service-Provider-Id'] = currentServiceProviderOptional.value.id.toString();
    }

    return headers;
};

export default createClient({
    url: '/api/graphql',
    fetch: customFetch,
    exchanges: [
        cacheExchange({
            keys: {
                ShipmentWarehouse: data => String(data.id),
                StockMove: data => String(data.uuid),
                TrackingNumber: data => data.id != null ? String(data.id) : null,
                ItemGroupConfiguration: data => String(data.configuration_id),
                PaginatorInfo: () => null,
                ShipmentItemRowPaginator: () => null,
                InboundShipmentPaginator: () => null,
                InboundShipmentStatistics: () => null,
                OutboundShipmentPaginator: () => null,
                OutboundShipmentStatistics: () => null,
                OutboundShipmentTagCount: () => null,
            },
        }),
        mapExchange({
            onOperation(operation) {
                addBreadcrumb({
                    type: 'http',
                    category: 'graphql.request',
                    message: `${operation.kind} ${getOperationName(operation.query)}`,
                    data: {
                        operationName: getOperationName(operation.query),
                        kind: operation.kind,
                    },
                    level: 'info',
                });

                if (operation.context.omitMerchantHeader != null) {
                    return {
                        ...operation,
                        context: {
                            ...operation.context,
                            fetchOptions: (): RequestInit => {
                                const headers = getHeaders();
                                delete headers['X-Selected-Client-Id'];
                                return { headers };
                            },
                        },
                    };
                }
            },
            onResult(result) {
                const operationName = getOperationName(result.operation.query);

                if (result.error) {
                    addBreadcrumb({
                        type: 'http',
                        category: 'graphql.response',
                        message: `graphql error: ${operationName}`,
                        data: {
                            operationName,
                            error: result.error.message,
                            graphQLErrors: result.error.graphQLErrors.map(e => e.message),
                            networkError: result.error.networkError?.message,
                        },
                        level: 'error',
                    });
                } else {
                    addBreadcrumb({
                        type: 'http',
                        category: 'graphql.response',
                        message: `graphql ok: ${operationName}`,
                        data: { operationName },
                        level: 'info',
                    });
                }
            },
        }),
        lighthouseSubscriptionExchange('/api/graphql', getHeaders),
        fetchExchange,
    ],
    preferGetMethod: false,
    fetchOptions: (): RequestInit => {
        return { headers: getHeaders() };
    },
});
